import React, { Component, type ComponentType } from 'react';
import { styled as styled2 } from '@compiled/react';
import Avatar, { type AppearanceType } from '@atlaskit/avatar';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import WorldIcon from '@atlaskit/icon/core/globe';
import { token } from '@atlaskit/tokens';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	PUBLIC_SHARE,
	PROJECT_SHARE,
	PROJECT_UNKNOWN_SHARE,
	GROUP_SHARE,
	OPEN_SHARE,
	USER_SHARE,
	type SharePermission,
} from '@atlassian/jira-shared-types/src/share-permission.tsx';
import messages from './messages.tsx';

const assertUnreachable = (_x: never): never => {
	throw new Error("Didn't expect to get here");
};

export type Props = {
	shares: SharePermission[];
} & Intl;

// eslint-disable-next-line jira/react/no-class-components
export class Access extends Component<Props> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static defaultProps = {
		shares: [],
	};

	static renderAvatar(
		text: string,
		avatarUrl: string,
		idx: number,
		appearance: AppearanceType,
	): React.JSX.Element {
		return (
			<Container key={idx}>
				<AvatarContainer>
					<Avatar size="small" src={avatarUrl} appearance={appearance} />
				</AvatarContainer>
				<TextContainer>{text}</TextContainer>
			</Container>
		);
	}

	static renderIcon(
		text: string,
		IconComponent: ComponentType<{
			label: '';
			spacing: 'spacious' | 'none';
		}>,
		idx = 1,
	): React.JSX.Element {
		return (
			<Container key={idx}>
				<IconContainer>
					<IconComponent label="" spacing="spacious" />
				</IconContainer>
				<TextContainer>{text}</TextContainer>
			</Container>
		);
	}

	render(): React.JSX.Element {
		const {
			shares,
			intl: { formatMessage },
		} = this.props;

		return (
			<CellContainer>
				{shares.length > 0
					? shares.map((share: SharePermission, idx) => {
							switch (share.type) {
								case OPEN_SHARE:
									return Access.renderIcon(
										formatMessage(messages.myOrganizationAccess),
										OfficeBuildingIcon,
										idx,
									);
								case PUBLIC_SHARE:
									return Access.renderIcon(formatMessage(messages.publicAccess), WorldIcon, idx);
								case GROUP_SHARE:
									// @ts-expect-error - TS2345 - Type 'undefined' is not assignable to type 'string'
									return Access.renderIcon(share.group.name, PeopleGroupIcon, idx);
								case USER_SHARE:
									return Access.renderAvatar(
										share.user.displayName,
										share.user.avatarUrl,
										idx,
										'circle',
									);
								case PROJECT_SHARE: {
									const roleName = share.role
										? share.role.name
										: formatMessage(messages.allProjectRoles);
									const text = `${share.project.name}, ${roleName}`;

									return Access.renderAvatar(text, share.project.avatarUrl, idx, 'square');
								}
								case PROJECT_UNKNOWN_SHARE:
									return null; // hide from the list
								default:
									assertUnreachable(share);
									return null;
							}
						})
					: Access.renderIcon(formatMessage(messages.privateAccess), LockIcon)}
			</CellContainer>
		);
	}
}

export default injectIntl(Access);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.div({
	display: 'flex',
	alignItems: 'center',
	paddingBottom: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarContainer = styled2.div({
	paddingRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled2.div({
	paddingTop: token('space.025'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled2.div({
	wordWrap: 'break-word',
	flexGrow: 1,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellContainer = styled2.div({
	paddingTop: token('space.075'),
	display: 'flex',
	flexDirection: 'column',
});
